import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Sparkle, Backspace } from "@phosphor-icons/react";

const PIN_LENGTH = 4;
const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "del"];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const press = (k) => {
    if (loading) return;
    setError("");
    if (k === "del") setPin((p) => p.slice(0, -1));
    else if (k) setPin((p) => (p.length < PIN_LENGTH ? p + k : p));
  };

  useEffect(() => {
    const onKey = (e) => {
      if (/^[0-9]$/.test(e.key)) press(e.key);
      else if (e.key === "Backspace") press("del");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  useEffect(() => {
    if (pin.length !== PIN_LENGTH) return;
    setLoading(true);
    login(pin)
      .then(() => navigate("/"))
      .catch((err) => {
        const d = err.response?.data?.detail;
        setError(typeof d === "string" ? d : "Échec de la connexion");
        setPin("");
      })
      .finally(() => setLoading(false));
  }, [pin]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1762625570087-6d98fca29531?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtZWRpY2FsJTIwY2xpbmljJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg1NDQwNzAxfDA&ixlib=rb-4.1.0&q=85"
          alt="Clínica Valorian" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-brand/70" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2 font-mono text-xs tracking-[0.3em] uppercase">
            <Sparkle weight="fill" size={16} /> Clínica Valorian
          </div>
          <div>
            <h1 className="font-serif text-5xl leading-tight">Moteur éditorial<br />autonome SEO / GEO</h1>
            <p className="mt-4 text-white/80 max-w-md text-sm leading-relaxed">
              Une rédaction augmentée par IA qui surveille la science, crée et optimise en continu,
              sous votre validation médicale.
            </p>
          </div>
          <div className="font-mono text-[11px] tracking-widest text-white/50 uppercase">Marbella · Medicina basada en la evidencia</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-bone">
        <div className="w-full max-w-xs text-center" data-testid="login-form">
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-muted-foreground mb-2">Espace validateur médical</div>
          <h2 className="font-serif text-3xl text-brand mb-8">Code PIN</h2>

          <div className="flex justify-center gap-4 mb-4" data-testid="pin-dots">
            {Array.from({ length: PIN_LENGTH }).map((_, i) => (
              <span key={i} className={`w-4 h-4 rounded-full border-2 border-brand transition-colors ${i < pin.length ? "bg-brand" : "bg-transparent"} ${loading ? "ai-pulse" : ""}`} />
            ))}
          </div>
          <div className="h-5 mb-4">
            {error && <div data-testid="login-error" className="text-sm text-red-600">{error}</div>}
          </div>

          <div className="grid grid-cols-3 gap-3">
            {KEYS.map((k, i) => k === "" ? <span key={i} /> : (
              <button key={i} type="button" data-testid={k === "del" ? "pin-key-del" : `pin-key-${k}`} onClick={() => press(k)}
                disabled={loading} aria-label={k === "del" ? "Effacer" : k}
                className="h-16 rounded-xl bg-white border border-border text-2xl font-serif text-brand hover:bg-brand-soft active:scale-95 transition-transform disabled:opacity-60 flex items-center justify-center">
                {k === "del" ? <Backspace size={24} /> : k}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
